import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { AccordionContent, AccordionItem, AccordionTrigger } from "../../../../components/shadcn/accordion";
import { motion } from "framer-motion";
import { CurrencySelectItemDisplay } from "../Routes";
import { CollapsableHeader } from "./CollapsableHeader";
import { StickyHeader } from "./StickyHeader";
import clsx from "clsx";
import { NavigatableItem, NavigatableRegistrationContext, useNavigatableListState } from "../../../../components/NavigatableList";
import { useVirtualizer } from "../../../../lib/virtual";
import { defaultRangeExtractor } from "../../../../lib/virtual/core";
export const CollapsibleRow = ({ item, index, toggleContent, direction, onSelect, selectedRoute, selectedToken, searchQuery, openValues, scrollContainerRef, hideBalances, }) => {
    const groupName = item.type === "grouped_token" ? item.symbol : item.route.name;
    const headerId = `${groupName}-header`;
    const childrenList = useMemo(() => {
        if (item.type === "grouped_token") {
            const grouped = item;
            return grouped.items.map((el) => ({
                token: el.route.token,
                route: el.route.route,
            }));
        }
        else {
            const route = item.route;
            return route.tokens.map((t) => ({
                token: t,
                route: route,
            }));
        }
    }, [item]);
    const [isSticky, setSticky] = useState(false);
    const headerRef = useRef(null);
    const contentRef = useRef(null);
    const isOpen = openValues?.some((ov) => ov === groupName);
    const registration = useContext(NavigatableRegistrationContext);
    const childrenCount = childrenList?.length ?? 0;
    useEffect(() => {
        if (!registration || !isOpen)
            return;
        registration.setChildCount(index, childrenCount);
        return () => registration.setChildCount(index, undefined);
    }, [registration, index, isOpen, childrenCount]);
    const stickyToggle = () => {
        toggleContent(groupName);
        headerRef.current?.scrollIntoView({ block: "start", inline: "start" });
    };
    const setHeaderRef = (el) => {
        headerRef.current = el;
    };
    return (_jsx(motion.div
    //  {...(!searchQuery && { layout: "position" })} 
    , { children: _jsxs(AccordionItem, { value: groupName, children: [_jsx(NavigatableItem, { index: index, onClick: () => toggleContent(groupName), className: clsx("cursor-pointer rounded-lg relative group/accordion hover:bg-secondary-500", isSticky && "opacity-0"), focusedClassName: "bg-secondary-500 is-focused", children: _jsx("div", { ref: setHeaderRef, id: headerId, children: _jsx(AccordionTrigger, { tabIndex: -1, children: _jsx(CollapsableHeader, { item: item, direction: direction, isOpen: isOpen, hideBalances: hideBalances }) }) }) }), _jsx(StickyHeader, { item: item, direction: direction, scrollContainer: scrollContainerRef.current, open: isOpen, headerRef: headerRef, contentRef: contentRef, childrenCount: childrenList?.length, onClick: stickyToggle, isSticky: isSticky, setSticky: setSticky, hideBalances: hideBalances }), _jsx(AccordionContent, { className: "AccordionContent", ref: contentRef, itemsCount: childrenList?.length, children: _jsx("div", { className: "has-[.token-item]:mt-1 bg-secondary-500 rounded-xl overflow-hidden", children: _jsx("div", { className: "overflow-y-auto styled-scroll p-2", children: _jsx(VirtualTokenList, { childrenList: childrenList, groupName: groupName, parentIndex: index, selectedRoute: selectedRoute, selectedToken: selectedToken, direction: direction, onSelect: onSelect, scrollContainerRef: scrollContainerRef, hideBalances: hideBalances }) }) }) })] }) }, searchQuery ? "search" : "default"));
};
// Only the visible tokens are mounted; networks like Ethereum have 1000+ tokens.
const VirtualTokenList = ({ childrenList, groupName, parentIndex, selectedRoute, selectedToken, direction, onSelect, scrollContainerRef, hideBalances, }) => {
    const listRef = useRef(null);
    const [scrollMargin, setScrollMargin] = useState(0);
    const registration = useContext(NavigatableRegistrationContext);
    const { focusedIndex } = useNavigatableListState();
    const navigableParentIndex = registration?.getNavigableIndex(parentIndex) ?? -1;
    const effectiveParentIndex = navigableParentIndex >= 0 ? navigableParentIndex : parentIndex;
    const focusedChildIndex = focusedIndex?.parent === effectiveParentIndex ? focusedIndex.child : undefined;
    // Keep the focused token mounted so its scroll-into-view effect and Enter handler still work.
    const rangeExtractor = useCallback((range) => {
        const indexes = defaultRangeExtractor(range);
        if (focusedChildIndex === undefined || focusedChildIndex >= range.count || indexes.includes(focusedChildIndex)) {
            return indexes;
        }
        return [...indexes, focusedChildIndex].sort((a, b) => a - b);
    }, [focusedChildIndex]);
    // The list sits below other rows inside the shared scroll container, so its offset must be measured.
    useLayoutEffect(() => {
        const list = listRef.current;
        const scroller = scrollContainerRef.current;
        if (!list || !scroller)
            return;
        const offset = list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
        setScrollMargin((prev) => (Math.abs(prev - offset) < 1 ? prev : offset));
    });
    const virtualizer = useVirtualizer({
        count: childrenList.length,
        estimateSize: () => 52,
        getScrollElement: () => scrollContainerRef.current,
        // The virtualizer scrolls to initialOffset on mount; keep the shared container where it is.
        initialOffset: () => scrollContainerRef.current?.scrollTop ?? 0,
        scrollMargin,
        overscan: 10,
        rangeExtractor,
    });
    const items = virtualizer.getVirtualItems();
    return (_jsx("div", { ref: listRef, style: { height: virtualizer.getTotalSize(), position: "relative" }, children: items.map((virtualRow) => {
            const { token, route } = childrenList[virtualRow.index];
            const isSelected = selectedRoute === route.name && selectedToken === token.symbol;
            return (_jsx("div", { "data-index": virtualRow.index, ref: virtualizer.measureElement, style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    // Per-row offsets: the pinned focused token can make the range non-contiguous.
                    transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                }, children: _jsx(TokenItem, { token: token, route: route, childIndex: virtualRow.index, groupName: groupName, parentIndex: parentIndex, isSelected: isSelected, direction: direction, onSelect: onSelect, hideBalances: hideBalances }) }, `${groupName}-${virtualRow.index}`));
        }) }));
};
// Memoized child item to prevent re-renders when siblings change focus
const TokenItem = memo(({ token, route, childIndex, groupName, parentIndex, isSelected, direction, onSelect, hideBalances, }) => {
    const handleClick = useCallback(() => {
        onSelect(route, token);
    }, [onSelect, route, token]);
    return (_jsx(NavigatableItem, { index: childIndex, parentIndex: parentIndex, onClick: handleClick, className: "token-item pl-2 pr-3 cursor-pointer rounded-xl outline-none disabled:cursor-not-allowed hover:bg-secondary-400", focusedClassName: "bg-secondary-400", children: _jsx(CurrencySelectItemDisplay, { item: token, selected: isSelected, route: route, direction: direction, hideBalances: hideBalances }) }, `${groupName}-${childIndex}`));
});
